import { useState, useEffect } from 'react';
import {
  Question, Lightning, AddressBook, MagnifyingGlass, Calendar, Printer,
  Users, ChartBar, FunnelSimple, Notebook, CaretRight, Keyboard, Shield, FilmStrip
} from '@phosphor-icons/react';

const VIDEO_TUTORIALS = [
  { src: '/videocrunch-lookup.mp4', poster: '/videocrunch-poster-lookup.jpg', title: 'Lookup — filter your contacts in seconds' },
  { src: '/videocrunch-smart-import.mp4', poster: '/videocrunch-poster-smart-import.jpg', title: 'Smart Import — a whole spreadsheet in under a minute' },
  { src: '/videocrunch-mail-merge.mp4', poster: '/videocrunch-poster-mail-merge.jpg', title: 'Mail Merge — a personalized letter for every contact' },
  { src: '/videocrunch-crunchy.mp4', poster: '/videocrunch-poster-crunchy.jpg', title: 'Meet Crunchy — the AI assistant that knows your data' },
  { src: '/videocrunch-lookup-smartgroup.mp4', poster: '/videocrunch-poster-lookup-smartgroup.jpg', title: 'Stacked Lookups + Save as Smart Group' },
  { src: '/videocrunch-quick-add.mp4', poster: '/videocrunch-poster-quick-add.jpg', title: 'Quick Add — a contact in 5 seconds (Ctrl+K)' },
  { src: '/videocrunch-crunchy-bulk-email.mp4', poster: '/videocrunch-poster-crunchy-bulk-email.jpg', title: 'Crunchy drafts a bulk email campaign from one sentence' },
];

const ARTICLES = [
  {
    id: 'video-tutorials',
    icon: FilmStrip,
    title: 'Video tutorials (60 seconds each)',
    videos: VIDEO_TUTORIALS,
    body: [],
  },
  {
    id: 'getting-started',
    icon: Lightning,
    title: 'Getting started in 60 seconds',
    body: [
      { h: 'Welcome to SalesCrunchCRM', p: 'If you ever used ACT! — the classic contact manager — you\'re about to feel right at home. Everything is on one screen: contacts, activities, calendar, pipeline, and reports. No hidden menus, no "settings within settings."' },
      { h: '1. Open the Contacts tab', p: 'Click Contacts in the left sidebar. You\'ll see the ACT!-style 3-column contact card: Business Card · Address · Status.' },
      { h: '2. Use the toolbar', p: 'The toolbar at the top has buttons for: Previous/Next, New, Call, Meeting, Note, Email, Save. Every action is one click.' },
      { h: '3. Press Ctrl+K (or Cmd+K on Mac)', p: 'Quick Add dialog from anywhere. Useful when a prospect is on the phone — add them in 3 seconds.' },
      { h: '4. Try Boolean Lookup', p: 'Click the Lookup button in the top right. Pick a field (State, Company, Status…), an operator (= ~ * <> < > <= >=), and a value. Narrow or broaden results as you go.' },
    ],
  },
  {
    id: 'contacts',
    icon: AddressBook,
    title: 'Managing contacts',
    body: [
      { h: 'Create a contact', p: 'Click "New" in the toolbar. Fill in the fields (Tab moves between them). Click Save.' },
      { h: 'Log an activity', p: 'With a contact open, click the Call, Meeting, Note, or Email button. Fill in title, date, priority, and optional details. Click OK.' },
      { h: 'Schedule recurring activities', p: 'In the activity dialog, check "Make recurring". Choose Daily / Weekly / Bi-weekly / Monthly / Yearly and how many occurrences (up to 52). Each one will appear on your Task List and Calendar automatically.' },
      { h: 'Private contacts', p: 'Click the Lock icon in the toolbar to toggle between Public and Private. Private contacts are only visible to you and admins.' },
      { h: 'Tags', p: 'Add tags in the Business Card area for flexible grouping ("VIP", "Q2-2026", "referral"). Filter by tag on the Contact List.' },
    ],
  },
  {
    id: 'boolean-lookup',
    icon: MagnifyingGlass,
    title: 'Boolean Lookup — all 8 operators',
    body: [
      { h: '= Equals', p: 'Exact match. Example: State = "CA"' },
      { h: '~ Contains', p: 'The tilde. Example: Company ~ "Tech" matches "TechVentures", "FinTech Labs", etc.' },
      { h: '* Wildcard', p: 'The asterisk. Example: LastName = "Sm*" matches Smith, Smalley, Smithson.' },
      { h: '<> Not Equals', p: 'Example: Status <> "Inactive"' },
      { h: '< > <= >=', p: 'Comparison. Example: ZIP > "90000" for West Coast; CreatedAt >= "2026-01-01"' },
      { h: 'Narrow vs Broaden', p: 'Narrow = "show me results that also match this" (AND logic). Broaden = "add more to current results" (OR logic).' },
      { h: 'Quick tip', p: 'After a lookup, the status bar shows "Lookup: [description]" and the contact counter is limited to matches.' },
    ],
  },
  {
    id: 'keyword-search',
    icon: MagnifyingGlass,
    title: 'Global Keyword Search',
    body: [
      { h: 'What it searches', p: 'Search bar in sidebar → searches across contact names, companies, emails, notes, phones, AND every activity\'s title/content — all at once.' },
      { h: 'When to use it', p: 'When you don\'t remember WHERE you stored info. "I know I logged a call about a price objection three months ago…" just type "price objection".' },
      { h: 'Privacy-aware', p: 'Private contacts owned by other users won\'t show up for you (unless you\'re Admin/Manager).' },
    ],
  },
  {
    id: 'calendar-tasks',
    icon: Calendar,
    title: 'Calendar & Task List',
    body: [
      { h: 'Calendar views', p: 'Monthly grid, weekly column view, daily hourly view. Click any day to see activities. Drag activities between days to reschedule.' },
      { h: 'Task List filters', p: 'Filter by type (call/meeting/to-do), priority (high/med/low), date range, and completed/open.' },
      { h: 'Completing tasks', p: 'Click the checkbox to mark complete. Past completed tasks stay in your History tab on the contact.' },
    ],
  },
  {
    id: 'pipeline',
    icon: FunnelSimple,
    title: 'Sales Pipeline (Kanban)',
    body: [
      { h: '6 stages', p: 'Lead → Qualified → Proposal → Negotiation → Won · Lost. Drag cards between columns as deals progress.' },
      { h: 'Totals', p: 'Each column header shows count + total $ value. Watch your pipeline size shift in real time.' },
      { h: 'Link to contacts', p: 'Every deal links to a contact. Click the deal to jump to that contact\'s card.' },
    ],
  },
  {
    id: 'templates-merge',
    icon: Notebook,
    title: 'Templates, Mail Merge & Printing',
    body: [
      { h: 'Merge fields', p: 'Use {first_name}, {last_name}, {company}, {email}, {phone}, {salutation}, {address_street}, {address_city}, {address_state}, {address_zip}, {title}, {status}, {date} anywhere in a template.' },
      { h: 'Bulk preview', p: 'Print & Merge → Mail Merge tab → pick template → select contacts (or a group) → "Preview / Print" opens printable letters, one per page.' },
      { h: 'Actually sending emails', p: 'Click "Send Emails" instead. Each contact receives a personalized email. Every send auto-logs as an email activity on their history.' },
      { h: 'Labels', p: 'Avery 5160 (30/sheet), 5161 (20), 5162 (14), 5163 (10). Address info auto-formatted.' },
      { h: 'Envelopes', p: '#10 business, A2 invite, A7 card. Add a return address once — prints on every envelope.' },
    ],
  },
  {
    id: 'groups',
    icon: Users,
    title: 'Groups & Segmentation',
    body: [
      { h: 'Create a group', p: 'Groups page → New Group → name it ("Q2 Event Attendees", "Customers - Texas", etc.).' },
      { h: 'Add contacts', p: 'From the Contact Card → Groups tab → pick from dropdown. Or bulk-select on the Contact List and assign.' },
      { h: 'Use groups for merge', p: 'On Print & Merge, pick a group instead of hand-selecting — it pulls everyone in the group.' },
    ],
  },
  {
    id: 'realtime',
    icon: Users,
    title: 'Real-time Collaboration',
    body: [
      { h: 'Presence indicator', p: 'When a teammate views the same contact, you\'ll see a green pulse dot and their name in the top right of the toolbar.' },
      { h: 'Live updates', p: 'When they save changes, your card refreshes automatically — no manual reload.' },
      { h: 'Update banner', p: 'If you were mid-edit when someone else saved, you\'ll see a banner: "[Name] just updated this contact — click to reload".' },
    ],
  },
  {
    id: 'reports',
    icon: ChartBar,
    title: 'Reports',
    body: [
      { h: 'Contact Report', p: 'Full details per contact with activity history. Great for filing.' },
      { h: 'Directory', p: 'Alphabetical address book format.' },
      { h: 'Phone List', p: 'Company / Contact / Phone / Mobile — the "laminate and keep on your desk" report.' },
      { h: 'Activity History', p: 'All calls/meetings/notes in a date range, across all contacts.' },
      { h: 'History Summary', p: 'Count of calls, emails, meetings, notes per contact. Shows who you\'ve neglected.' },
      { h: 'Print', p: 'Every report has a Print button → opens a clean printable view.' },
    ],
  },
  {
    id: 'shortcuts',
    icon: Keyboard,
    title: 'Keyboard shortcuts',
    body: [
      { h: 'Ctrl+K / Cmd+K', p: 'Quick Add a contact from anywhere.' },
      { h: 'Tab', p: 'Move between fields on a contact card.' },
      { h: 'Esc', p: 'Close any open dialog.' },
    ],
  },
  {
    id: 'privacy-roles',
    icon: Shield,
    title: 'Privacy & Roles',
    body: [
      { h: 'Shared database', p: 'By default everyone on your team sees all contacts (the ACT! 4.0 paradigm).' },
      { h: 'Private contacts', p: 'Click the Lock icon to mark a contact Private. Only you and admins see private contacts in lists, searches, and reports.' },
      { h: 'Roles', p: 'Admin = full access + settings. Manager = see everything, no settings. User = see all public + own private; no settings access.' },
    ],
  },
];

export default function HelpPage() {
  const [open, setOpen] = useState('getting-started');

  useEffect(() => {
    // Mark tour as seen so the contacts banner stops showing
    localStorage.setItem('seen_tour', '1');
  }, []);

  return (
    <div className="flex h-full bg-[#E8E8E8]" data-testid="help-page">
      {/* Left TOC */}
      <aside className="w-72 bg-white border-r border-[#CCC] overflow-y-auto shrink-0">
        <div className="bg-gradient-to-b from-[#F39C12] to-[#D68910] px-4 py-2 border-b border-[#999] sticky top-0 z-10">
          <h1 className="text-[14px] font-bold text-white flex items-center gap-2">
            <Question size={16} weight="bold" />Help & Training
          </h1>
        </div>
        <nav className="py-2">
          {ARTICLES.map(a => {
            const Icon = a.icon;
            const active = open === a.id;
            return (
              <button
                key={a.id}
                onClick={() => setOpen(a.id)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-[12px] border-l-4 transition-colors ${
                  active ? 'border-[#F39C12] bg-[#FFF8EC] text-[#0B5394] font-semibold' : 'border-transparent hover:bg-[#F5F5F5] text-[#333]'
                }`}
                data-testid={`help-toc-${a.id}`}
              >
                <Icon size={14} weight="bold" className={active ? 'text-[#F39C12]' : 'text-[#777]'} />
                <span className="flex-1">{a.title}</span>
                <CaretRight size={10} weight="bold" className="text-[#999]" />
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Right Content */}
      <main className="flex-1 overflow-y-auto p-8" data-testid="help-content">
        {ARTICLES.filter(a => a.id === open).map(a => {
          const Icon = a.icon;
          return (
            <article key={a.id} className="max-w-3xl mx-auto">
              <header className="flex items-center gap-3 mb-6 pb-3 border-b border-[#CCC]">
                <div className="w-10 h-10 bg-[#0B5394] rounded flex items-center justify-center">
                  <Icon size={20} weight="bold" className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-[#111]">{a.title}</h2>
              </header>
              <div className="space-y-5">
                {a.videos && (
                  <div className="grid gap-6" data-testid="help-video-grid">
                    {a.videos.map(v => (
                      <div key={v.src} className="bg-white border border-[#CCC] shadow-sm overflow-hidden" data-testid={`help-video-${v.src}`}>
                        <video controls preload="metadata" poster={v.poster} className="w-full aspect-video bg-black">
                          <source src={v.src} type="video/mp4" />
                        </video>
                        <div className="px-4 py-3 text-[13px] font-bold text-[#0B5394]">{v.title}</div>
                      </div>
                    ))}
                  </div>
                )}
                {a.body.map((sec, i) => (
                  <section key={i} className="bg-white border border-[#CCC] p-5 shadow-sm" data-testid={`help-section-${a.id}-${i}`}>
                    <h3 className="text-[14px] font-bold text-[#0B5394] mb-2">{sec.h}</h3>
                    <p className="text-[13px] text-[#333] leading-relaxed whitespace-pre-line">{sec.p}</p>
                  </section>
                ))}
              </div>
              <footer className="mt-8 text-[12px] text-[#666] italic">
                Can't find what you need? Email support at chip@migrationcrunch.com.
              </footer>
            </article>
          );
        })}
      </main>
    </div>
  );
}
